import { Injectable } from '@angular/core';

import { PDFDocument } from 'pdf-lib';

import { HttpClient } from '@angular/common/http';
import { Observable, combineLatest, defer, from, map, switchMap, withLatestFrom } from 'rxjs';
import {
  AddressDetails,
  DevicesStore,
  InstallationDetailsStore,
  InstallationFeaturesStore,
  OwnerDetails,
} from '@store';
import {
  DcSensorValueProperties,
  FeatureNames,
  FlowLimitTemperatureCommands,
  FlowLimitTemperatureProperties,
  IsEnabledProperty,
  OwnershipType,
} from '@vi-iot/client/index';
import { VdzFormHelper } from './src-vdz-form.helper';

@Injectable({
  providedIn: 'root',
})
export class SrcVdzFormService {
  constructor(
    private http: HttpClient,
    private installationFeaturesStore: InstallationFeaturesStore,
    private devicesStore: DevicesStore,
    private installationDetailsStore: InstallationDetailsStore
  ) {}

  // Return path to the modified .pdf file
  processPdfFile(): Observable<string> {
    return this.getPdfFormDocument().pipe(
      withLatestFrom(this.getAggregatedData()),
      switchMap(
        ([
          pdfDocument,
          {
            name,
            address,
            waterPressure,
            maxFlowTemperatureArray,
            hcTemperaturesLoweredArray,
            volumetricFlow,
          },
        ]) => {
          const form = pdfDocument.getForm();
          // todo private method

          const mappedAddress: string[] = this.addressMapper(address);

          VdzFormHelper.vdzFields.forEach((field) => {
            if (field.type === VdzFormHelper.VdzFieldType.PDFTextField) {
              const textField = form.getTextField(field.name);
              switch (field.name) {
                // Name of the owner
                case 'Name Antragsteller':
                  textField.setText(name);
                  break;
                // Address of the owner
                case 'Straße Nummer Antragsteller':
                  textField.setText(mappedAddress[0]);
                  break;
                case 'PLZ Ort Antragsteller':
                  if (mappedAddress[1]) {
                    textField.setText(mappedAddress[1]);
                  }
                  break;
                // Address of the installation
                case 'Objektanschrift Zeile 1':
                  textField.setText(mappedAddress[0]);
                  break;
                case 'Objektanschrift Zeile 2':
                  if (mappedAddress[1]) {
                    textField.setText(mappedAddress[1]);
                  }
                  break;
                // Water pressure
                case 'Fülldruck bar':
                  const waterPressureText = waterPressure?.toString();
                  if (waterPressure) {
                    textField.setText(waterPressureText);
                  }
                  break;
                // Note
                case 'Bemerkungen':
                  textField.setText(VdzFormHelper.annotationText);
                  break;
              }
            }
            // Checkboxes always selected
            const fixedCheckboxFields: VdzFormHelper.VdzFieldName[] = [
              'B',
              'Ausdehnungsgefäß geprüft',
            ];
            if (
              field.type === VdzFormHelper.VdzFieldType.PDFCheckBox &&
              fixedCheckboxFields.includes(field.name)
            ) {
              const checkbox = form.getCheckBox(field.name);

              checkbox.check();
            }
          });

          // Data depending on heating circuit presence
          // If mapped temperature is not returned treat it as a missing feature
          maxFlowTemperatureArray.forEach((maxFlowTemperature: number, i: number) => {
            const index = i + 1;

            if (maxFlowTemperature !== undefined) {
              // heating.circuits.N.temperature.levels/max
              const textFieldMaxTemperature = form.getTextField(
                `Heizkreis ${index} Auslegungsvorlauftemperatur`
              );
              textFieldMaxTemperature.setText(maxFlowTemperature.toString());

              // Fixed checkbox
              const checkboxTwoPipeHeatingSystem = form.getCheckBox(
                `heizkreis ${index} Zweirohrheizung`
              );
              checkboxTwoPipeHeatingSystem.check();
            }

            // heating.circuits.N.temperature.levels/max minus 15
            const loweredTemperature = hcTemperaturesLoweredArray[i];

            if (loweredTemperature !== undefined) {
              const textFieldLoweredTemperature = form.getTextField(
                `Heizkreis ${index} Heizkreisrücklauftemperatur`
              );
              textFieldLoweredTemperature.setText(loweredTemperature?.toString());
            }

            // heating.sensors.volumetricFlow.allengra
            if ((maxFlowTemperature || loweredTemperature) && volumetricFlow !== undefined) {
              const textFieldVolumetricFlow = form.getTextField(
                `Heizkreis ${index} Gesamtdurchfluss`
              );
              textFieldVolumetricFlow.setText(volumetricFlow?.toString() || '');
            }
          });

          return defer(() => from(pdfDocument.save()));
        }
      ),
      map((pdfBytes) => {
        const blob = new Blob([pdfBytes], { type: 'application/pdf' });

        return URL.createObjectURL(blob);
      })
    );
  }

  // Split address in 2 inputs if it's too long
  private addressMapper = (address: string): string[] => {
    const limit = 60;

    if (address.length > limit) {
      const i = address.lastIndexOf(' ', limit);

      const part1 = address.slice(0, i);
      const part2 = address.slice(i + 1);

      return [part1, part2].filter((el) => el);
    } else {
      return [address];
    }
  };

  private getPdfFormDocument(): Observable<PDFDocument> {
    return this.http
      .get(VdzFormHelper.assetPath, { responseType: 'arraybuffer' })
      .pipe(
        switchMap((data) => defer(() => from(PDFDocument.load(data, { ignoreEncryption: true }))))
      );
  }

  private getAggregatedData(): Observable<VdzFormHelper.VdzFormMappedData> {
    const ownerAndAddress$: Observable<{
      address: AddressDetails;
      owner: OwnerDetails;
    }> = this.installationDetailsStore.ownershipType$.pipe(
      switchMap((ownershipType) => {
        if (
          ownershipType === OwnershipType.CommercialV1 ||
          ownershipType === OwnershipType.CommercialV2
        ) {
          return combineLatest([
            this.installationDetailsStore.consumerAddress$,
            this.installationDetailsStore.consumer$,
          ]);
        } else {
          return combineLatest([
            this.installationDetailsStore.address$,
            this.installationDetailsStore.owner$,
          ]);
        }
      }),
      map(([address, owner]) => ({ address, owner }))
    );

    const waterPressure$: Observable<number> = this.installationFeaturesStore
      .selectInstallationDeviceFeature<DcSensorValueProperties, never>(FeatureNames.waterPressure)
      .pipe(map((feature) => feature?.properties?.value?.value));

    const hcTemperatures$: Observable<VdzFormHelper.VdzFormHeatingCircuitValues> = this.installationFeaturesStore
      .selectInstallationDeviceFeature<IsEnabledProperty, never>(FeatureNames.circuits)
      .pipe(
        switchMap((circuitsFeature) => {
          const featureCircuitIds = circuitsFeature?.properties?.enabled?.value || [];

          const maxTemperatures: Observable<number>[] = featureCircuitIds.map((circuitId) =>
            this.installationFeaturesStore
              .selectInstallationDeviceFeature<
                FlowLimitTemperatureProperties,
                FlowLimitTemperatureCommands
              >(FeatureNames.flowLimitTemperature(circuitId))
              .pipe(
                map((temperatureLevelFeature) => temperatureLevelFeature?.properties?.max?.value)
              )
          );

          return combineLatest(maxTemperatures).pipe();
        }),
        map(
          (temperatures): VdzFormHelper.VdzFormHeatingCircuitValues => {
            const offsetConstant = 15;

            return {
              max1: temperatures[0],
              max2: temperatures[1],
              max3: temperatures[2],
              //  Subtraction by 15 is required by the ticket
              hc1Lowered: !isNaN(temperatures[0]) ? temperatures[0] - offsetConstant : undefined,
              hc2Lowered: !isNaN(temperatures[1]) ? temperatures[1] - offsetConstant : undefined,
              hc3Lowered: !isNaN(temperatures[2]) ? temperatures[2] - offsetConstant : undefined,
            };
          }
        )
      );

    const volumetricFlow$: Observable<number> = this.devicesStore.getSelectedDevice$.pipe(
      switchMap((device) =>
        this.installationFeaturesStore
          .selectInstallationDeviceFeature<DcSensorValueProperties, never>(
            FeatureNames.volumetricFlowReturn,
            device.id
          )
          .pipe(map((feature) => feature?.properties?.value?.value))
      )
    );

    const output$: Observable<VdzFormHelper.VdzFormMappedData> = combineLatest([
      ownerAndAddress$,
      waterPressure$,
      hcTemperatures$,
      volumetricFlow$,
    ]).pipe(
      map(
        ([
          { address, owner },
          waterPressure,
          hcTemperatures,
          volumetricFlow,
        ]): VdzFormHelper.VdzFormMappedData => {
          return {
            name: owner.name,
            address: address.street + ' ' + address.city,
            waterPressure,
            maxFlowTemperatureArray: [
              hcTemperatures.max1,
              hcTemperatures.max2,
              hcTemperatures.max3,
            ],
            hcTemperaturesLoweredArray: [
              hcTemperatures.hc1Lowered,
              hcTemperatures.hc2Lowered,
              hcTemperatures.hc3Lowered,
            ],
            volumetricFlow,
          };
        }
      )
    );

    return output$;
  }
}
